import { Button, Space } from "antd";
import { SUB_ACCOUNT_STATE } from "@zhihaoscm/meta";
import { dateTimeFormat } from "@zhihaoscm/utils";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subManagementService } from "@/api/subManagementService";
import { Breadcrumb, ProTable, ZhMessageBox, type ProColumn } from "@/components/common";
import { useUserSession } from "@/stores/session";

interface InvitedRow {
    account?: { institutionName?: string };
    customerEnterprise?: { id: number; state?: number; createdTime?: string };
}
type InviteAction = { type: "authenticate" | "confirm" | "reject"; id?: number };
/** 受邀记录页，未实名用户确认邀请前必须先进入个人认证。 */
export function InvitationRecordPage() {
    const [refreshKey, setRefreshKey] = useState(0);
    const [action, setAction] = useState<InviteAction>();
    const realName = useUserSession(state => state.userInfo.customer.realName);
    const navigate = useNavigate();
    /** 已实名时确认邀请，未实名时显示认证引导。 */
    const confirmInvite = (id: number) => {
        setAction(realName ? { type: "confirm", id } : { type: "authenticate" });
    };
    /** 拒绝邀请后刷新记录，避免继续操作已拒绝邀请。 */
    const rejectInvite = (id: number) => setAction({ type: "reject", id });
    /** 按旧版 zhMessageBox 的 then 语义执行动作，成功后关闭并刷新表格。 */
    const submitAction = async () => {
        if (!action) return;
        if (action.type === "authenticate") {
            setAction(undefined);
            navigate("/me/info");
            return;
        }
        if (action.type === "confirm") await subManagementService.confirm(action.id!, SUB_ACCOUNT_STATE.CONFIRMED.code);
        else await subManagementService.reject(action.id!, SUB_ACCOUNT_STATE.REJECTED.code);
        setAction(undefined);
        setRefreshKey(value => value + 1);
    };
    const columns = useMemo<ProColumn<InvitedRow>[]>(
        () => [
            {
                key: "institutionName",
                title: "企业名称",
                minWidth: 142,
                search: {
                    key: "insititutionName",
                    type: "input",
                    placeholder: "请输入企业名称搜索",
                    span: 5,
                    width: 120
                },
                render: (_, row) => row.account?.institutionName || "--"
            },
            {
                key: "createdTime",
                title: "受邀时间",
                minWidth: 142,
                render: (_, row) =>
                    row.customerEnterprise?.createdTime ? dateTimeFormat(row.customerEnterprise.createdTime) : "--"
            },
            {
                key: "state",
                title: "状态",
                minWidth: 142,
                search: {
                    key: "state",
                    type: "select",
                    placeholder: "请选择状态",
                    span: 3,
                    width: 80,
                    options: SUB_ACCOUNT_STATE.options
                },
                render: (_, row) =>
                    SUB_ACCOUNT_STATE.options.find(item => item.value === row.customerEnterprise?.state)?.label ?? "--"
            },
            {
                key: "operation",
                title: "操作",
                fixed: "right",
                render: (_, row) =>
                    row.customerEnterprise?.state === SUB_ACCOUNT_STATE.NOT_CONFIRMED.code ? (
                        <Space size={12}>
                            <Button type="link" onClick={() => confirmInvite(row.customerEnterprise!.id)}>
                                确认
                            </Button>
                            <Button type="link" onClick={() => rejectInvite(row.customerEnterprise!.id)}>
                                拒绝
                            </Button>
                        </Space>
                    ) : null
            }
        ],
        [realName]
    );
    const tip =
        action?.type === "authenticate"
            ? "成为企业子账号请先完成个人实名认证"
            : action?.type === "confirm"
              ? "是否确认？ 确认后可以切换至对应企业"
              : "是否拒绝？ 拒绝后将不在受邀记录中展示";
    return (
        <section className="invitation-record-page section-card">
            <Breadcrumb routes={[{ name: "账号管理" }, { name: "受邀记录" }]} />
            <div className="invitation-record-table">
                <ProTable
                    key={refreshKey}
                    title="受邀记录"
                    rowKey={row => row.customerEnterprise?.id ?? 0}
                    columns={columns}
                    request={params => subManagementService.getInvitedList(params as any)}
                />
            </div>
            <ZhMessageBox
                open={Boolean(action)}
                tip={tip}
                submitText={action?.type === "authenticate" ? "去认证" : undefined}
                onCancel={() => setAction(undefined)}
                onSubmit={submitAction}
            />
        </section>
    );
}
